import type { Person, Relationship } from './types'

/**
 * Lenses (v0.21.0, D-124) — focused views over ONE family graph.
 *
 * The problem this solves, in the keeper's words: "my father may have 3 brothers
 * and a sister and I do not want to clutter the Rash Line with all those" — while
 * still recording them. That is a VIEW problem, not a storage problem. Splitting
 * the data into more trees is what caused the fragmentation incident (D-109): five
 * relatives each rebuilt the same people in separate trees.
 *
 * So: one graph, many lenses. `selectSubgraph` returns a filtered {persons,
 * relationships} that `layoutTree` renders unchanged — every existing layout
 * invariant keeps holding because the layout engine never learns this exists.
 */

export type LensKind =
  | 'everyone'      // the whole archive (previous behaviour)
  | 'close'         // you, your parents, siblings, spouse, children
  | 'extended'      // + grandparents, grandchildren, nieces/nephews, aunts/uncles, cousins
  | 'ancestors'     // pedigree: direct ancestors only — no uncles, no cousins
  | 'descendants'   // you and everyone descended from you
  | 'hourglass'     // ancestors above, descendants below
  | 'paternal'      // your father's side
  | 'maternal'      // your mother's side

export type LensOpts = {
  kind: LensKind
  focusId?: string
  depth?: number          // generations up/down (default 4)
  collaterals?: boolean   // include siblings of ancestors (and their children)
}

export const LENS_LABELS: Record<LensKind, string> = {
  everyone: 'Everyone',
  close: 'Close family',
  extended: 'Extended family',
  ancestors: 'My ancestors',
  descendants: 'My descendants',
  hourglass: 'Me, up & down',
  paternal: "Father's side",
  maternal: "Mother's side",
}

const parentsOf = (id: string, rels: Relationship[]) =>
  rels.filter(r => r.type === 'parent' && r.person2_id === id).map(r => r.person1_id)
const childrenOf = (id: string, rels: Relationship[]) =>
  rels.filter(r => r.type === 'parent' && r.person1_id === id).map(r => r.person2_id)
const spousesOf = (id: string, rels: Relationship[]) =>
  rels.filter(r => r.type === 'spouse' && (r.person1_id === id || r.person2_id === id))
    .map(r => (r.person1_id === id ? r.person2_id : r.person1_id))

/** Walk up or down, depth-limited and cycle-safe (bad data can contain loops). */
function walk(startIds: string[], rels: Relationship[], dir: 'up' | 'down', depth: number): Set<string> {
  const seen = new Set<string>(startIds)
  let frontier = startIds
  for (let d = 0; d < depth && frontier.length; d++) {
    const next: string[] = []
    for (const id of frontier) {
      for (const n of (dir === 'up' ? parentsOf(id, rels) : childrenOf(id, rels))) {
        if (!seen.has(n)) { seen.add(n); next.push(n) }
      }
    }
    frontier = next
  }
  return seen
}

/** Siblings = anyone sharing at least one parent (there are no explicit sibling rows). */
function siblingsOf(id: string, rels: Relationship[]): string[] {
  const out = new Set<string>()
  for (const p of parentsOf(id, rels)) {
    for (const c of childrenOf(p, rels)) if (c !== id) out.add(c)
  }
  return [...out]
}

export function selectSubgraph(
  persons: Person[], relationships: Relationship[], opts: LensOpts,
): { persons: Person[]; relationships: Relationship[] } {
  const rels = relationships.filter(r => r.type === 'parent' || r.type === 'spouse')
  const depth = Math.max(1, opts.depth ?? 4)
  const focus = opts.focusId

  if (opts.kind === 'everyone' || !focus || !persons.some(p => p.id === focus)) {
    return { persons, relationships }
  }

  let keep = new Set<string>([focus])

  switch (opts.kind) {
    case 'close': {
      for (const id of [...parentsOf(focus, rels), ...childrenOf(focus, rels),
                        ...siblingsOf(focus, rels), ...spousesOf(focus, rels)]) keep.add(id)
      break
    }
    case 'extended': {
      // two rings out: grandparents and grandchildren, the siblings of you AND of
      // your parents (aunts/uncles), plus their children (nieces/nephews, cousins)
      const up1 = parentsOf(focus, rels)
      const up2 = up1.flatMap(p => parentsOf(p, rels))
      const sibs = siblingsOf(focus, rels)
      const kids = childrenOf(focus, rels)
      for (const id of [...up1, ...up2, ...sibs, ...kids, ...spousesOf(focus, rels)]) keep.add(id)
      for (const s of sibs) for (const n of childrenOf(s, rels)) keep.add(n)      // nieces & nephews
      for (const k of kids) for (const g of childrenOf(k, rels)) keep.add(g)      // grandchildren
      for (const p of up1) {
        for (const aunt of siblingsOf(p, rels)) {
          keep.add(aunt)
          for (const cousin of childrenOf(aunt, rels)) keep.add(cousin)           // cousins
        }
      }
      break
    }
    case 'ancestors':
      keep = walk([focus], rels, 'up', depth)
      break
    case 'descendants':
      keep = walk([focus], rels, 'down', depth)
      break
    case 'hourglass': {
      keep = walk([focus], rels, 'up', depth)
      for (const id of walk([focus], rels, 'down', depth)) keep.add(id)
      break
    }
    case 'paternal':
    case 'maternal': {
      // the side is chosen by the parent's gender; we keep that parent and everything
      // above them. Unknown gender is never guessed — it simply isn't that side.
      const wanted = opts.kind === 'paternal' ? 'male' : 'female'
      const byId = new Map(persons.map(p => [p.id, p]))
      const start = parentsOf(focus, rels).filter(pid => byId.get(pid)?.gender === wanted)
      keep = new Set<string>([focus])
      for (const id of walk(start, rels, 'up', depth)) keep.add(id)
      break
    }
  }

  // collaterals: the siblings of everyone on the kept ancestral path (and their
  // children) — off by default, because they are precisely the "clutter" complained of
  if (opts.collaterals) {
    for (const id of [...keep]) {
      for (const sib of siblingsOf(id, rels)) {
        keep.add(sib)
        for (const kid of childrenOf(sib, rels)) keep.add(kid)
      }
    }
  }

  // include spouses of kept people so couples render as couples — but do NOT pull in
  // the spouse's own ancestors (that is a different family's line, and how a focused
  // view silently becomes the whole archive again)
  for (const id of [...keep]) for (const sp of spousesOf(id, rels)) keep.add(sp)

  return {
    persons: persons.filter(p => keep.has(p.id)),
    // an edge survives only if BOTH ends survive — this is what prevents the dangling
    // edges the graph engine had to be taught about server-side (D-077)
    relationships: relationships.filter(r => keep.has(r.person1_id) && keep.has(r.person2_id)),
  }
}

/** How many people a lens hides — used to offer "showing 8 of 36". */
export function lensCounts(total: number, shown: number) {
  return { total, shown, hidden: Math.max(0, total - shown) }
}
